import React, { useEffect, useState, useCallback } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { View, Text, ImageBackground, StyleSheet } from "react-native";
import { onSnapshot, doc, getFirestore } from "firebase/firestore";
import CookiePolicyPopup from "../Screens/Helpers/CookiePolicyPopup";
import AnalyticsManager from '../services/AnalyticsManager';
import ResourcesButton from "./ResourcesButton";
import { SpotlightTutorialProvider } from "./SpotlightTutorial/SpotlightTutorialProvider";
import ReferralSurveyGate from "./Onboarding/ReferralSurveyGate";

import Landing from "../Screens/Landing";
import Profile from "../Screens/Profile";
import Hub from "../Screens/Hub";
import Admin from "../Screens/Admin";
import Events from "../Screens/Events";
import Registry from "../Screens/Registry";
import Catalogue from "../Screens/RegistryWidgets/Catalogue";
import Privacy from "../Screens/Helpers/Privacy";
import Terms from "../Screens/Helpers/Terms";
import Checklist from "../Screens/Helpers/Checklist";
import VerifyEmail from "../Screens/VerifyEmail";
import Blog from "../Screens/Blog";
import Tutorials from "../Screens/TutorialsScreen";
import WhyMyTassel from "../Screens/WhyMyTassel";
import ContactUs from "../Screens/ContactUs";
import GraduateDashboard from "../Screens/GraduateDashboard";
import Unsubscribe from "../Screens/Unsubscribe";
import KeychainRedirect from "../Screens/KeychainRedirect";
import ECardViewer from "../Screens/ECardViewer";

const Stack = createStackNavigator();
const linking = {
	prefixes: ["https://mytassel.com", "mytassel://", "http://localhost:"],
	config: {
		screens: {
			"My Tassel - Welcome": "",
			Staging: "staging",
			Hub: "hub",
			Profile: "profile",
			Admin: {
				path: "admin/:tab?",
				parse: { tab: (tab) => `${tab}` },
			},
			Blog: "blog",
			Tutorials: "tutorials",
			Catalogue: "catalogue",
			Keychain: "keychain",
			WhyMyTassel: "why-mytassel",
			Privacy: "privacy",
			Terms: "terms",
			ContactUs: "contact-us",
			Checklist: {
				path: "checklist/:type",
				parse: {
					type: (type) => `${type}`,
				},
			},
			Announcements: {
				path: "events/:uid",
				parse: {
					uid: (uid) => `${uid}`,
				},
			},
			Registry: {
				path: "registry/:uid",
				parse: {
					uid: (uid) => `${uid}`,
				},
			},
			VerifyEmail: {
				path: "verify-email",
			},
			Unsubscribe: {
				path: "unsubscribe",
			},
			ECardViewer: {
				path: "ecard/:deliveryId",
				parse: {
					deliveryId: (deliveryId) => `${deliveryId}`,
				},
			},
			GraduateDashboard: {
				path: "dashboard/:uid",
				parse: {
					uid: (uid) => `${uid}`,
				},
			},
			VanityRegistry: {
				path: ":slug/registry",
				parse: {
					slug: (slug) => `${slug}`,
				},
			},
			AnnouncementPage: {
				path: ":slug",
				parse: {
					slug: (slug) => `${slug}`,
				},
			},
		},
	},
};

const Navigation = () => {
	const [isMaintenance, setIsMaintenance] = useState(false);
	const [maintenanceStartTime, setMaintenanceStartTime] = useState(null);
	const [currentRoute, setCurrentRoute] = useState("My Tassel - Welcome");
	const [isReady, setIsReady] = useState(false);
	const [maintenanceMode, setMaintenanceMode] = useState(false);
	const [disableRegistration, setDisableRegistration] = useState(false);
	const [showCookiePopup, setShowCookiePopup] = useState(false);

	const db = getFirestore();

	const isProduction = process.env.NODE_ENV === 'production';

	// Initialize analytics
	useEffect(() => {
		// Initialize improved analytics service
		AnalyticsManager.init();
	}, []);

	// Check for maintenance mode
	useEffect(() => {
		const unsubscribe = onSnapshot(doc(db, "settings", "maintenance"), (doc) => {
			if (doc.exists()) {
				const data = doc.data();
				setMaintenanceMode(data.enabled || false);
				setDisableRegistration(data.disableRegistration || false);
			}
		});

		return () => unsubscribe();
	}, []);

	// Check for cookie consent
	useEffect(() => {
		const cookieConsent = localStorage.getItem("cookieConsent");
		if (!cookieConsent) {
			setShowCookiePopup(true);
		}
	}, []);

	useEffect(() => {
		const statusDocRef = doc(db, "maintenance", "status");
		const unsubscribe = onSnapshot(statusDocRef, (doc) => {
			if (doc.exists()) {
				setIsMaintenance(doc.data().isMaintenanceActive);
				setMaintenanceStartTime(doc.data().startTime);
			}
		});
		return () => unsubscribe();
	}, []);

	useEffect(() => {
		const initializeNavigation = async () => {
			setIsReady(true);
		};

		initializeNavigation();
	}, []);

	const handleStateChange = useCallback((state) => {
		const currentRouteName = state?.routes[state.index]?.name || 'Unknown';
		const routeParams = state?.routes[state.index]?.params || {};
		
		// Create specific page identifier
		let specificPageName = currentRouteName;
		let pageContext = currentRouteName;
		
		// Make page names more specific based on route parameters
		if (currentRouteName === 'Registry' && routeParams.uid) {
			specificPageName = `Registry-${routeParams.uid}`;
			pageContext = `Registry of User ${routeParams.uid}`;
		} else if (currentRouteName === 'Announcements' && routeParams.uid) {
			specificPageName = `Events-${routeParams.uid}`;
			pageContext = `Events of User ${routeParams.uid}`;
		} else if (currentRouteName === 'Checklist' && routeParams.type) {
			specificPageName = `Checklist-${routeParams.type}`;
			pageContext = `${routeParams.type} Checklist`;
		}
		
		// Page tracking is handled by usePageTracking hook in individual pages
		// to avoid duplicate tracking entries

		if (currentRouteName) {
			setCurrentRoute(currentRouteName);
		}
	}, []);

	if (!isReady) {
		return null;
	}

	return (
		<NavigationContainer linking={linking} onStateChange={handleStateChange}>
			<SpotlightTutorialProvider>
				{isProduction && isMaintenance && currentRoute !== "Admin" && (
					<ImageBackground source={require("../assets/Landing Background.png")} style={styles.backgroundImage}>
						<View style={styles.maintenanceMessage}>
							<Text style={styles.maintenanceText}>
								{maintenanceStartTime
									? `The site is currently undergoing a scheduled maintenance as of ${maintenanceStartTime}. Please check back later.`
									: "The site is currently undergoing a scheduled maintenance. Please check back later."
								}
							</Text>
						</View>
					</ImageBackground>
				)}
				<Stack.Navigator
					initialRouteName="My Tassel - Welcome"
					screenOptions={{
						headerShown: false
					}}
				>
					<Stack.Screen name="My Tassel - Welcome" component={Landing} />
					<Stack.Screen name="Staging" component={Landing} />
	
					{(!isProduction || !isMaintenance) && (
						<>
							<Stack.Screen name="Hub" component={Hub} />
							<Stack.Screen name="Profile" component={Profile} />
							<Stack.Screen 
								name="Announcements" 
								component={Events} 
								initialParams={{ uid: "" }} 
							/>
							<Stack.Screen
								name="AnnouncementPage"
								component={Events}
								initialParams={{ slug: "" }}
							/>
							<Stack.Screen 
								name="Registry" 
								component={Registry} 
								initialParams={{ uid: "" }} 
							/>
							<Stack.Screen
								name="VanityRegistry"
								component={Registry}
								initialParams={{ slug: "" }}
							/>
							<Stack.Screen name="Catalogue" component={Catalogue} />
							<Stack.Screen 
								name="Checklist" 
								component={Checklist} 
								initialParams={{ type: undefined }} 
							/>
							<Stack.Screen name="WhyMyTassel" component={WhyMyTassel} />
							<Stack.Screen
								name="ECardViewer"
								component={ECardViewer}
								initialParams={{ deliveryId: "" }}
							/>
							<Stack.Screen name="Privacy" component={Privacy} />
							<Stack.Screen name="Terms" component={Terms} />
							<Stack.Screen name="VerifyEmail" component={VerifyEmail} />
							<Stack.Screen name="Unsubscribe" component={Unsubscribe} />
							<Stack.Screen name="Tutorials" component={Tutorials} />
							<Stack.Screen name="Blog" component={Blog} />
							<Stack.Screen name="ContactUs" component={ContactUs} />
							<Stack.Screen name="GraduateDashboard" component={GraduateDashboard} />
							<Stack.Screen name="Keychain" component={KeychainRedirect} />
						</>
					)}
					<Stack.Screen name="Admin" component={Admin} />
				</Stack.Navigator>
				{showCookiePopup && <CookiePolicyPopup onAccept={() => setShowCookiePopup(false)} />}
				<ResourcesButton />
				<ReferralSurveyGate />
			</SpotlightTutorialProvider>
		</NavigationContainer>
	);
};

const styles = StyleSheet.create({
	backgroundImage: {
		flex: 1,
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 1000,
	},
	maintenanceMessage: {
		padding: 20,
		backgroundColor: 'yellow',
	},
	maintenanceText: {
		fontWeight: 'bold',
	},
});

export default Navigation;
